import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import './improvedhero.css';

// Images — same path convention, filenames match course names
import StaadProImage from '../assets/Image/about/staadpro.jpg';
import StaadProStructuralImage from '../assets/Image/about/staadpro_structural.jpg';
import MicroStationImage from '../assets/Image/about/microstation_infra.jpg';
import MicroStationInfraImage from '../assets/Image/about/microstation_infra.jpg';
import OpenRoadsImage from '../assets/Image/about/openroads.jpg';
import OpenRoadsHighwayImage from '../assets/Image/about/openroads_highway.jpg';
import WaterGEMSImage from '../assets/Image/about/watergems.jpg';
import SewerGEMSImage from '../assets/Image/about/sewergems.jpg';
import SewerGEMSStormwaterImage from '../assets/Image/about/sewergems_stormwater.jpg';
import BentleyOverviewImage from '../assets/Image/about/bentley_overview.jpg';

const heroSlides = [
    {
        image: StaadProImage,
        badge: 'STAAD PRO',
        title: 'Structural analysis and design',
        titleAccent: 'trusted by engineers worldwide.',
        description:
            'Analyse and design structures of any size, shape, or complexity with STAAD.Pro — the industry-leading software for steel, concrete, timber, and aluminium structures.',
        link: '/course/staad-pro',
    },
    {
        image: StaadProStructuralImage,
        badge: 'STAAD PRO FOR STRUCTURAL ENGINEERING',
        title: 'Advanced tools for structural engineers,',
        titleAccent: 'detailers and analysts.',
        description:
            'Model, analyse, and design multi-material structures with code compliance across 90+ international design codes.',
        link: '/course/staad-pro',
    },
    {
        image: MicroStationImage,
        badge: 'MICROSTATION',
        title: '2D and 3D design and modeling',
        titleAccent: 'for infrastructure professionals.',
        description:
            'A powerful CAD platform for detailed design, drafting, and 3D modeling of infrastructure assets — from concept to construction.',
        link: '/course/microstation',
    },
    {
        image: MicroStationInfraImage,
        badge: 'MICROSTATION FOR INFRASTRUCTURE',
        title: 'Precise modeling for roads, bridges',
        titleAccent: 'and utility networks.',
        description:
            'Create accurate, information-rich 3D models that integrate seamlessly with other Bentley applications across your project lifecycle.',
        link: '/course/microstation',
    },
    {
        image: OpenRoadsImage,
        badge: 'OPENROADS DESIGNER',
        title: 'Comprehensive detailed design for',
        titleAccent: 'civil infrastructure.',
        description:
            'OpenRoads Designer: An integrated detailed design and modeling solution for roadway, corridor, and site development projects.',
        link: '/course/openroads-designer',
    },
    {
        image: OpenRoadsHighwayImage,
        badge: 'OPENROADS DESIGNER FOR HIGHWAYS',
        title: 'Model highways and roadways',
        titleAccent: 'with real-world context.',
        description:
            'Design complex corridors, intersections, and interchanges faster with dynamic cross-sections and automated design standards.',
        link: '/course/openroads-designer',
    },
    {
        image: WaterGEMSImage,
        badge: 'OPENFLOWS WATERGEMS',
        title: 'Water distribution modeling',
        titleAccent: 'made simple and powerful.',
        description:
            'Design, analyse, and optimise water distribution systems with hydraulic and water quality modeling trusted by utilities worldwide.',
        link: '/course/watergems',
    },
    {
        image: SewerGEMSImage,
        badge: 'OPENFLOWS SEWERGEMS',
        title: 'Sanitary and combined sewer',
        titleAccent: 'system modeling software.',
        description:
            'Plan, design, and manage sanitary and combined sewer systems with integrated hydraulic and hydrologic modeling.',
        link: '/course/sewergems',
    },
    {
        image: SewerGEMSStormwaterImage,
        badge: 'OPENFLOWS SEWERGEMS FOR STORMWATER',
        title: 'Model stormwater networks',
        titleAccent: 'and prevent urban flooding.',
        description:
            'Simulate storm events and evaluate system capacity to design resilient stormwater infrastructure for growing cities.',
        link: '/course/sewergems',
    },
    {
        image: BentleyOverviewImage,
        badge: 'BENTLEY AUTHORIZED TRAINING',
        title: 'Master the tools trusted by',
        titleAccent: 'top infrastructure firms globally.',
        description:
            'From structural analysis to civil design and water infrastructure — build in-demand Bentley software skills with hands-on, industry-focused training.',
        link: '/about',
    },
];

// Image crossfade duration in ms — keep in sync with CSS transition value
const FADE_DURATION = 2500;
const SLIDE_INTERVAL = 5000;

function ImprovedHeroSection() {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [contentKey, setContentKey] = useState(0);
    const timeoutRef = useRef(null);
    const contentTimeoutRef = useRef(null);

    useEffect(() => {
        const nextIndex = (currentIndex + 1) % heroSlides.length;

        timeoutRef.current = setTimeout(() => {
            // Switch image and text at exactly the same moment
            setCurrentIndex(nextIndex);
            setContentKey((k) => k + 1);
        }, SLIDE_INTERVAL);

        return () => {
            clearTimeout(timeoutRef.current);
            clearTimeout(contentTimeoutRef.current);
        };
    }, [currentIndex]);

    const handleDotClick = (index) => {
        // On manual dot click: switch image and text simultaneously
        setCurrentIndex(index);
        setContentKey((k) => k + 1);
        clearTimeout(timeoutRef.current);
        clearTimeout(contentTimeoutRef.current);
    };

    const slide = heroSlides[currentIndex];

    return (
        <section className="improved-hero-section" aria-label="Bentley hero section">
            <div className="hero-images">
                {heroSlides.map((s, index) => (
                    <img
                        key={index}
                        src={s.image}
                        alt={`${s.badge} background`}
                        className={`hero-image ${currentIndex === index ? 'active' : 'inactive'}`}
                    />
                ))}
                <div className="hero-overlay" />
            </div>

            <div className="hero-content">
                <div className="hero-text" key={contentKey}>
                    <p className="hero-badge">{slide.badge}</p>
                    <h1 className="hero-title">
                        {slide.title}
                        <span> {slide.titleAccent}</span>
                    </h1>
                    <p className="hero-description">{slide.description}</p>

                    <div className="hero-cta-row">
                        <Link to={slide.link}>
                            <button type="button" className="hero-cta primary">
                                Explore Courses
                                <ArrowRight size={18} />
                            </button>
                        </Link>
                    </div>
                </div>
            </div>

            <div className="hero-dots" role="tablist" aria-label="Hero Slides">
                {heroSlides.map((_, index) => (
                    <button
                        key={index}
                        type="button"
                        className={`dot ${currentIndex === index ? 'active' : 'inactive'}`}
                        onClick={() => handleDotClick(index)}
                        aria-label={`Go to slide ${index + 1}`}
                    />
                ))}
            </div>
        </section>
    );
}

export default ImprovedHeroSection;